import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import { Api } from '../api/api';
import Loader from './Loader';

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  overflow: visible;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const PageTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 1.3;
  color: #FFFFFF;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  width: 100%;
  gap: 32px;
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const MainMetricCard = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #24BA7D 0%, #1C9162 100%);
  border-radius: 24px;
  padding: 32px;
  flex: 1;
  gap: 12px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
`;

const SecondaryMetricCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #172A2A;
  border: 1px solid #1F3737;
  border-radius: 24px;
  padding: 32px;
  flex: 1;
  gap: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #24BA7D;
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(36, 186, 125, 0.15);
  }
`;

const MetricValue = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 1.2;
  color: #FFFFFF;
  z-index: 1;
`;

const MetricLabel = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  z-index: 1;
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #172A2A;
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  gap: 24px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ChartTitle = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.5;
  color: #FFFFFF;
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #1F3737;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
`;

const PeriodText = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43;
  color: #FFFFFF;
`;

const ChartContent = styled.div`
  display: flex;
  width: 100%;
  height: 300px;
  background-color: #172A2A;
  position: relative;

  .recharts-bar-rectangle:hover {
    fill: #1C9162 !important;
    opacity: 0.6;
  }

  .recharts-active-bar .recharts-rectangle {
    fill: #1C9162 !important;
    opacity: 0.6;
  }

  &:hover {
    background-color: rgba(23, 42, 42, 0.5);
  }
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #172A2A;
  border-radius: 24px;
  padding: 32px;
  gap: 24px;
  flex: 1;
  overflow: visible;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  overflow: visible;
`;

const SectionTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.3;
  color: #FFFFFF;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1F3737;
    border-radius: 3px;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #24BA7D;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1C9162;
  }
`;

const TopItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #1F3737;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #24403F;
    transform: translateX(4px);
  }
`;

const TopItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const TopItemRank = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #24BA7D 0%, #1C9162 100%);
  border-radius: 50%;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #FFFFFF;
  flex-shrink: 0;
`;

const TopItemName = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: #FFFFFF;
  flex: 1;
`;

const TopItemValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TopItemCount = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.33;
  color: #24BA7D;
`;

const TopItemPercent = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.33;
  color: #609F94;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  text-align: center;
  background-color: #1F3737;
  border-radius: 8px;
  border: 1px dashed #2C4A4A;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  width: 100%;
  overflow: visible;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BestSalesTable = styled.div`
  background-color: #172A2A;
  border-radius: 24px;
  padding: 32px;
  overflow: hidden;
`;

const TableContainer = styled.div`
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1F3737;
    border-radius: 3px;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #24BA7D;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1C9162;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const THead = styled.thead`
  background-color: #172A2A;
`;

const TBody = styled.tbody``;

const TR = styled.tr`
  &:hover {
    background-color: rgba(31, 55, 55, 0.5);
  }
`;

const TH = styled.th`
  padding: 16px;
  text-align: left;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.43;
  color: #609F94;
  border-bottom: 1px solid #1F3737;
  white-space: nowrap;
`;

const TD = styled.td`
  padding: 16px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.43;
  color: #FFFFFF;
  border-bottom: 1px solid #1F3737;
  white-space: nowrap;
`;

const ProductCell = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductName = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43;
  color: #FFFFFF;
`;

const ProductModel = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.33;
  color: #609F94;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background-color: #1F3737;
  border-radius: 8px;
  overflow: hidden;
  z-index: 9999;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.43;
  color: #FFFFFF;
  cursor: pointer;
  
  &:hover {
    background-color: #172A2A;
  }
`;

const ArrowDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.86 4.7L6.99 9.89L12.14 4.7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface BestSale {
  profit_sum: number;
  model: string;
  color: string;
  article: string;
  size: string;
}

type PeriodType = 'week' | 'month' | 'year';

const SalesAnalytics: React.FC = () => {
  const [metricsData, setMetricsData] = useState<any>({ profit_sum: 0, abs_profit_sum: 0, sales_sum: 0 });
  const [chartData, setChartData] = useState<any[]>([]);
  const [sizeData, setSizeData] = useState<any[]>([]);
  const [brandData, setBrandData] = useState<any[]>([]);
  const [articleData, setArticleData] = useState<any[]>([]);
  const [modelData, setModelData] = useState<any[]>([]);
  const [bestSalesData, setBestSalesData] = useState<any[]>([]);
  const [articleSizeData, setArticleSizeData] = useState<any[]>([]);
  const [providersData, setProvidersData] = useState<{ name: string; count: number }[]>([]);
  const [buyersData, setBuyersData] = useState<{ name: string; count: number }[]>([]);
  
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [sizeLoading, setSizeLoading] = useState(true);
  const [brandLoading, setBrandLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(true);
  const [modelLoading, setModelLoading] = useState(true);
  const [bestSalesLoading, setBestSalesLoading] = useState(true);
  const [articleSizeLoading, setArticleSizeLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [buyersLoading, setBuyersLoading] = useState(true);
  
  const [metricsPeriod, setMetricsPeriod] = useState<PeriodType>('month');
  const [chartPeriod, setChartPeriod] = useState<PeriodType>('month');
  const [sizePeriod, setSizePeriod] = useState<PeriodType>('month');
  const [brandPeriod, setBrandPeriod] = useState<PeriodType>('month');
  const [articlePeriod, setArticlePeriod] = useState<PeriodType>('month');
  const [modelPeriod, setModelPeriod] = useState<PeriodType>('month');
  const [bestSalesPeriod, setBestSalesPeriod] = useState<PeriodType>('month');
  const [articleSizePeriod, setArticleSizePeriod] = useState<PeriodType>('month');
  const [providersPeriod, setProvidersPeriod] = useState<PeriodType>('month');
  const [buyersPeriod, setBuyersPeriod] = useState<PeriodType>('month');
  
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [articleOpen, setArticleOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [bestSalesOpen, setBestSalesOpen] = useState(false);
  const [articleSizeOpen, setArticleSizeOpen] = useState(false);
  const [providersOpen, setProvidersOpen] = useState(false);
  const [buyersOpen, setBuyersOpen] = useState(false);
  
  const metricsRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<HTMLDivElement>(null);
  const brandRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);
  const bestSalesRef = useRef<HTMLDivElement>(null);
  const articleSizeRef = useRef<HTMLDivElement>(null);
  const providersRef = useRef<HTMLDivElement>(null);
  const buyersRef = useRef<HTMLDivElement>(null);
  
  const api = new Api();
  
  const periodLabels = {
    week: 'Неделя',
    month: 'Месяц',
    year: 'Год'
  };

  const loadMetrics = async (period: PeriodType) => {
    setMetricsLoading(true);
    try {
      const [profit, absProfit, allSales] = await Promise.all([
        api.getSalesProfit(period),
        api.getSalesAbsProfit(period),
        api.getAllSales(period)
      ]);
      
      setMetricsData({
        profit_sum: profit?.profit || 0,
        abs_profit_sum: absProfit?.profit_abs || 0,
        sales_sum: allSales?.sales_sum || 0
      });
    } catch (error) {
      console.error('Ошибка загрузки метрик:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadChartData = async (period: PeriodType) => {
    setChartLoading(true);
    try {
      const response = await api.getSalesDates(period);
      setChartData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных графика:', error);
    } finally {
      setChartLoading(false);
    }
  };

  const loadSizeData = async (period: PeriodType) => {
    setSizeLoading(true);
    try {
      const response = await api.getSalesBySize(period);
      setSizeData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных по размерам:', error);
    } finally {
      setSizeLoading(false);
    }
  };

  const loadBrandData = async (period: PeriodType) => {
    setBrandLoading(true);
    try {
      const response = await api.getSalesByBrand(period);
      setBrandData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных по брендам:', error);
    } finally {
      setBrandLoading(false);
    }
  };

  const loadArticleData = async (period: PeriodType) => {
    setArticleLoading(true);
    try {
      const response = await api.getSalesByArticle(period);
      setArticleData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных по артикулам:', error);
    } finally {
      setArticleLoading(false);
    }
  };

  const loadModelData = async (period: PeriodType) => {
    setModelLoading(true);
    try {
      const response = await api.getSalesByModel(period);
      setModelData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных по моделям:', error);
    } finally {
      setModelLoading(false);
    }
  };

  const loadBestSalesData = async (period: PeriodType) => {
    setBestSalesLoading(true);
    try {
      const response = await api.getBestSales(period);
      setBestSalesData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки лучших продаж:', error);
    } finally {
      setBestSalesLoading(false);
    }
  };

  const loadArticleSizeData = async (period: PeriodType) => {
    setArticleSizeLoading(true);
    try {
      const response = await api.getSalesByArticleAndSize(period);
      setArticleSizeData(response?.data || []);
    } catch (error) {
      console.error('Ошибка загрузки данных по артикулам и размерам:', error);
    } finally {
      setArticleSizeLoading(false);
    }
  };

  useEffect(() => {
    // Загружаем все данные при монтировании
    loadMetrics(metricsPeriod);
    loadChartData(chartPeriod);
    loadSizeData(sizePeriod);
    loadBrandData(brandPeriod);
    loadArticleData(articlePeriod);
    loadModelData(modelPeriod);
    loadBestSalesData(bestSalesPeriod);
    loadArticleSizeData(articleSizePeriod);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (metricsRef.current && !metricsRef.current.contains(event.target as Node)) {
        setMetricsOpen(false);
      }
      if (chartRef.current && !chartRef.current.contains(event.target as Node)) {
        setChartOpen(false);
      }
      if (sizeRef.current && !sizeRef.current.contains(event.target as Node)) {
        setSizeOpen(false);
      }
      if (brandRef.current && !brandRef.current.contains(event.target as Node)) {
        setBrandOpen(false);
      }
      if (articleRef.current && !articleRef.current.contains(event.target as Node)) {
        setArticleOpen(false);
      }
      if (modelRef.current && !modelRef.current.contains(event.target as Node)) {
        setModelOpen(false);
      }
      if (bestSalesRef.current && !bestSalesRef.current.contains(event.target as Node)) {
        setBestSalesOpen(false);
      }
      if (articleSizeRef.current && !articleSizeRef.current.contains(event.target as Node)) {
        setArticleSizeOpen(false);
      }
      if (providersRef.current && !providersRef.current.contains(event.target as Node)) {
        setProvidersOpen(false);
      }
      if (buyersRef.current && !buyersRef.current.contains(event.target as Node)) {
        setBuyersOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadProviders = async () => {
      setProvidersLoading(true);
      try {
        const res = await api.getAnalyticProviders(providersPeriod);
        setProvidersData(res?.data || []);
      } catch (e) {
        setProvidersData([]);
      } finally {
        setProvidersLoading(false);
      }
    };
    const loadBuyers = async () => {
      setBuyersLoading(true);
      try {
        const res = await api.getAnalyticBuyers(buyersPeriod);
        setBuyersData(res?.data || []);
      } catch (e) {
        setBuyersData([]);
      } finally {
        setBuyersLoading(false);
      }
    };
    loadProviders();
    loadBuyers();
  }, [providersPeriod, buyersPeriod]);

  const createPeriodHandler = (setter: (period: PeriodType) => void, loader: (period: PeriodType) => void, openSetter: (open: boolean) => void) => {
    return (period: PeriodType) => {
      openSetter(false);
      setter(period);
      loader(period);
    };
  };

  const applyPeriodToAllSections = (period: PeriodType) => {
    setChartPeriod(period);
    loadChartData(period);
    setSizePeriod(period);
    loadSizeData(period);
    setBrandPeriod(period);
    loadBrandData(period);
    setArticlePeriod(period);
    loadArticleData(period);
    setModelPeriod(period);
    loadModelData(period);
    setBestSalesPeriod(period);
    loadBestSalesData(period);
    setArticleSizePeriod(period);
    loadArticleSizeData(period);
    setProvidersPeriod(period);
    setBuyersPeriod(period);
  };

  const handleMetricsPeriodChange = (period: PeriodType) => {
    setMetricsOpen(false);
    setMetricsPeriod(period);
    loadMetrics(period);
    applyPeriodToAllSections(period);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#1F3737',
          border: '1px solid #24BA7D',
          borderRadius: '8px',
          padding: '12px',
          fontSize: '14px',
          color: '#FFFFFF'
        }}>
          <p style={{ margin: 0, marginBottom: '4px', color: '#609F94' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: {entry.name === 'Прибыль' ? 
                `${entry.value.toLocaleString()} ₽` : 
                `${entry.value} шт.`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString();
  };

  const getTopItems = (items: any[], limit?: number) => {
    if (!items || !Array.isArray(items)) return [];
    return limit ? items.slice(0, limit) : items;
  };

  const processChartData = (rawData: any[], period: PeriodType) => {
    if (!Array.isArray(rawData)) return [];
    
    if (period === 'week') {
      // Показать 7 дней недели
      const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
      const dayData = new Map();
      
      dayNames.forEach((day, index) => {
        dayData.set(index, { name: day, sales_count: 0, total_profit: 0 });
      });
      
      rawData.forEach(item => {
        const date = new Date(item.day);
        const dayIndex = (date.getDay() + 6) % 7; // Преобразуем воскресенье=0 в понедельник=0
        const existing = dayData.get(dayIndex);
        
        if (existing) {
          existing.sales_count += item.sales_count || 0;
          existing.total_profit += item.total_profit || 0;
        }
      });
      
      return Array.from(dayData.values());
    } else if (period === 'month') {
      // Показать 4 недели
      const weekNames = ['Неделя 1', 'Неделя 2', 'Неделя 3', 'Неделя 4'];
      const weekData = new Map();
      
      weekNames.forEach((week, index) => {
        weekData.set(index, { name: week, sales_count: 0, total_profit: 0 });
      });
      
      rawData.forEach(item => {
        const date = new Date(item.day);
        const dayOfMonth = date.getDate();
        const weekIndex = Math.min(Math.floor((dayOfMonth - 1) / 7), 3); // 0-3 недели
        const existing = weekData.get(weekIndex);
        
        if (existing) {
          existing.sales_count += item.sales_count || 0;
          existing.total_profit += item.total_profit || 0;
        }
      });
      
      return Array.from(weekData.values());
    } else {
      // Показать 12 месяцев для года
      const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
      ];
      
      const monthData = new Map();
      monthNames.forEach((month, index) => {
        monthData.set(index, { name: month, sales_count: 0, total_profit: 0 });
      });
      
      rawData.forEach(item => {
        const date = new Date(item.day);
        const monthIndex = date.getMonth();
        const existing = monthData.get(monthIndex);
        
        if (existing) {
          existing.sales_count += item.sales_count || 0;
          existing.total_profit += item.total_profit || 0;
        }
      });
      
      return Array.from(monthData.values());
    }
  };

  const calculatePercent = (value: number, total: number) => {
    if (!total || total === 0) return '0.0';
    return ((value / total) * 100).toFixed(1);
  };

  const columnHelper = createColumnHelper<BestSale>();
  const columns = [
    columnHelper.accessor('model', {
      header: 'Модель',
      cell: (info) => (
        <ProductCell>
          <ProductInfo>
            <ProductName>{info.getValue()}</ProductName>
            <ProductModel>{info.row.original.color}</ProductModel>
          </ProductInfo>
        </ProductCell>
      ),
      size: 200,
    }),
    columnHelper.accessor('article', {
      header: 'Артикул',
      cell: (info) => info.getValue(),
      size: 120,
    }),
    columnHelper.accessor('size', {
      header: 'Размер',
      cell: (info) => `US ${info.getValue()}`,
      size: 80,
    }),
    columnHelper.accessor('profit_sum', {
      header: 'Прибыль',
      cell: (info) => `${formatPrice(info.getValue())} ₽`,
      size: 120,
    }),
  ];

  const table = useReactTable({
    data: bestSalesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <StatsContainer>
      <PageHeader>
        <PageTitle>Статистика продаж</PageTitle>
      </PageHeader>

      {/* Основные метрики с селектором */}
      <StatsRow>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '20px' }}>Основные метрики</h3>
          <div ref={metricsRef} style={{ position: 'relative' }}>
            <PeriodSelector onClick={() => setMetricsOpen(!metricsOpen)}>
              <PeriodText>{periodLabels[metricsPeriod]}</PeriodText>
            <ArrowDownIcon />
          </PeriodSelector>
            {metricsOpen && (
            <DropdownMenu>
                <DropdownItem onClick={() => handleMetricsPeriodChange('week')}>
                Неделя
              </DropdownItem>
                <DropdownItem onClick={() => handleMetricsPeriodChange('month')}>
                Месяц
              </DropdownItem>
                <DropdownItem onClick={() => handleMetricsPeriodChange('year')}>
                Год
              </DropdownItem>
            </DropdownMenu>
          )}
        </div>
        </div>
      </StatsRow>

      <StatsRow>
        {metricsLoading ? (
        <MainMetricCard>
            <Loader size="small" noPadding />
          </MainMetricCard>
        ) : (
          <MainMetricCard>
            <MetricValue>{(metricsData.sales_sum || 0).toLocaleString()}</MetricValue>
          <MetricLabel>Общее количество продаж</MetricLabel>
        </MainMetricCard>
        )}
        
        {metricsLoading ? (
        <SecondaryMetricCard>
            <Loader size="small" noPadding />
          </SecondaryMetricCard>
        ) : (
          <SecondaryMetricCard>
            <MetricValue>{formatPrice(metricsData.profit_sum || 0)} ₽</MetricValue>
          <MetricLabel>Выручка</MetricLabel>
        </SecondaryMetricCard>
        )}
        
        {metricsLoading ? (
        <SecondaryMetricCard>
            <Loader size="small" noPadding />
          </SecondaryMetricCard>
        ) : (
          <SecondaryMetricCard>
            <MetricValue>{formatPrice(metricsData.abs_profit_sum || 0)} ₽</MetricValue>
          <MetricLabel>Прибыль</MetricLabel>
        </SecondaryMetricCard>
        )}
      </StatsRow>

      {/* График динамики с селектором */}
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>Динамика продаж</ChartTitle>
          <div ref={chartRef} style={{ position: 'relative' }}>
            <PeriodSelector onClick={() => setChartOpen(!chartOpen)}>
              <PeriodText>{periodLabels[chartPeriod]}</PeriodText>
              <ArrowDownIcon />
            </PeriodSelector>
            {chartOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => createPeriodHandler(setChartPeriod, loadChartData, setChartOpen)('week')}>
                  Неделя
                </DropdownItem>
                <DropdownItem onClick={() => createPeriodHandler(setChartPeriod, loadChartData, setChartOpen)('month')}>
                  Месяц
                </DropdownItem>
                <DropdownItem onClick={() => createPeriodHandler(setChartPeriod, loadChartData, setChartOpen)('year')}>
                  Год
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </ChartHeader>

        <ChartContent>
          {chartLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Loader size="medium" noPadding />
            </div>
          ) : chartData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <EmptyMessage style={{ backgroundColor: 'transparent', border: 'none' }}>
                Нет данных о продажах за данный период
              </EmptyMessage>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
                data={processChartData(chartData, chartPeriod)}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1F3737" />
              <XAxis
                  dataKey="name"
                stroke="#609F94"
                tick={{ fontSize: 12, fill: '#609F94' }}
                axisLine={{ stroke: '#1F3737' }}
                tickMargin={8}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke="#609F94"
                tick={{ fontSize: 12, fill: '#609F94' }}
                axisLine={{ stroke: '#1F3737' }}
                tickFormatter={(value) => `${value}`}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#609F94"
                tick={{ fontSize: 12, fill: '#609F94' }}
                axisLine={{ stroke: '#1F3737' }}
                tickFormatter={(value) => `${value.toLocaleString()}`}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="sales_count"
                name="Продажи"
                stroke="#24BA7D"
                fillOpacity={0.2}
                fill="#24BA7D"
                activeDot={{ r: 8 }}
              />
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="total_profit"
                name="Прибыль"
                stroke="#1C9162"
                fillOpacity={0.1}
                fill="#1C9162"
              />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </ChartContent>
      </ChartContainer>

      {/* Компактные списки с селекторами */}
      <GridLayout>
        {/* Продажи по размерам */}
        <SectionContainer>
          <SectionHeader>
          <SectionTitle>Продажи по размерам</SectionTitle>
            <div ref={sizeRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setSizeOpen(!sizeOpen)}>
                <PeriodText>{periodLabels[sizePeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {sizeOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => createPeriodHandler(setSizePeriod, loadSizeData, setSizeOpen)('week')}>
                    Неделя
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setSizePeriod, loadSizeData, setSizeOpen)('month')}>
                    Месяц
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setSizePeriod, loadSizeData, setSizeOpen)('year')}>
                    Год
                  </DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {sizeLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : sizeData.length === 0 ? (
            <EmptyMessage>
              Нет статистики по размерам за данный период
            </EmptyMessage>
          ) : (
          <TopList>
              {getTopItems(sizeData).map((item, index) => (
              <TopItem key={item.size}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>US {item.size}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.sales_count} продаж</TopItemCount>
                    <TopItemPercent>{calculatePercent(item.sales_count, metricsData.sales_sum)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
          )}
        </SectionContainer>

        {/* Продажи по брендам */}
        <SectionContainer>
          <SectionHeader>
          <SectionTitle>Продажи по брендам</SectionTitle>
            <div ref={brandRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setBrandOpen(!brandOpen)}>
                <PeriodText>{periodLabels[brandPeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {brandOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => createPeriodHandler(setBrandPeriod, loadBrandData, setBrandOpen)('week')}>
                    Неделя
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setBrandPeriod, loadBrandData, setBrandOpen)('month')}>
                    Месяц
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setBrandPeriod, loadBrandData, setBrandOpen)('year')}>
                    Год
                  </DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {brandLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : brandData.length === 0 ? (
            <EmptyMessage>
              Нет статистики по брендам за данный период
            </EmptyMessage>
          ) : (
          <TopList>
              {getTopItems(brandData).map((item, index) => (
              <TopItem key={item.brand}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.brand}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.sales_count} продаж</TopItemCount>
                    <TopItemPercent>{calculatePercent(item.sales_count, metricsData.sales_sum)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
          )}
        </SectionContainer>

        <SectionContainer>
          <SectionHeader>
          <SectionTitle>Лидирующие артикулы</SectionTitle>
            <div ref={articleRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setArticleOpen(!articleOpen)}>
                <PeriodText>{periodLabels[articlePeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {articleOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => createPeriodHandler(setArticlePeriod, loadArticleData, setArticleOpen)('week')}>
                    Неделя
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setArticlePeriod, loadArticleData, setArticleOpen)('month')}>
                    Месяц
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setArticlePeriod, loadArticleData, setArticleOpen)('year')}>
                    Год
                  </DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {articleLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : articleData.length === 0 ? (
            <EmptyMessage>
              Нет статистики по артикулам за данный период
            </EmptyMessage>
          ) : (
          <TopList>
              {getTopItems(articleData).map((item, index) => (
              <TopItem key={item.article}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.article}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.sales_count} продаж</TopItemCount>
                    <TopItemPercent>{calculatePercent(item.sales_count, metricsData.sales_sum)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
          )}
        </SectionContainer>

        {/* Продажи по моделям */}
        <SectionContainer>
          <SectionHeader>
          <SectionTitle>Топ модели</SectionTitle>
            <div ref={modelRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setModelOpen(!modelOpen)}>
                <PeriodText>{periodLabels[modelPeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {modelOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => createPeriodHandler(setModelPeriod, loadModelData, setModelOpen)('week')}>
                    Неделя
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setModelPeriod, loadModelData, setModelOpen)('month')}>
                    Месяц
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setModelPeriod, loadModelData, setModelOpen)('year')}>
                    Год
                  </DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {modelLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : modelData.length === 0 ? (
            <EmptyMessage>
              Нет статистики по моделям за данный период
            </EmptyMessage>
          ) : (
          <TopList>
              {getTopItems(modelData).map((item, index) => (
              <TopItem key={`${item.brand}-${item.model}`}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.model}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.sales_count} продаж</TopItemCount>
                    <TopItemPercent>{calculatePercent(item.sales_count, metricsData.sales_sum)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
          )}
        </SectionContainer>

        {/* Продажи по артикулам и размерам */}
        <SectionContainer>
          <SectionHeader>
          <SectionTitle>Артикулы и размеры</SectionTitle>
            <div ref={articleSizeRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setArticleSizeOpen(!articleSizeOpen)}>
                <PeriodText>{periodLabels[articleSizePeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {articleSizeOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => createPeriodHandler(setArticleSizePeriod, loadArticleSizeData, setArticleSizeOpen)('week')}>
                    Неделя
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setArticleSizePeriod, loadArticleSizeData, setArticleSizeOpen)('month')}>
                    Месяц
                  </DropdownItem>
                  <DropdownItem onClick={() => createPeriodHandler(setArticleSizePeriod, loadArticleSizeData, setArticleSizeOpen)('year')}>
                    Год
                  </DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
                     {articleSizeLoading ? (
             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
               <Loader size="small" noPadding />
             </div>
           ) : articleSizeData.length === 0 ? (
             <EmptyMessage>
               Нет статистики по артикулам и размерам за данный период
             </EmptyMessage>
           ) : (
           <TopList>
               {getTopItems(articleSizeData, 10).map((item, index) => (
               <TopItem key={`${item.article}-${item.size}`}>
                 <TopItemInfo>
                   <TopItemRank>{index + 1}</TopItemRank>
                   <TopItemName>{item.article} • US {item.size}</TopItemName>
                 </TopItemInfo>
                 <TopItemValue>
                   <TopItemCount>{item.sales_count} продаж</TopItemCount>
                     <TopItemPercent>{calculatePercent(item.sales_count, metricsData.sales_sum)}%</TopItemPercent>
                 </TopItemValue>
               </TopItem>
             ))}
           </TopList>
           )}
        </SectionContainer>

        {/* Поставщики */}
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Поставщики</SectionTitle>
            <div ref={providersRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setProvidersOpen(!providersOpen)}>
                <PeriodText>{periodLabels[providersPeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {providersOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => { setProvidersOpen(false); setProvidersPeriod('week'); }}>Неделя</DropdownItem>
                  <DropdownItem onClick={() => { setProvidersOpen(false); setProvidersPeriod('month'); }}>Месяц</DropdownItem>
                  <DropdownItem onClick={() => { setProvidersOpen(false); setProvidersPeriod('year'); }}>Год</DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {providersLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : providersData.length === 0 ? (
            <EmptyMessage>Нет статистики по поставщикам</EmptyMessage>
          ) : (
            <TopList>
              {providersData.sort((a, b) => b.count - a.count).map((item, idx) => (
                <TopItem key={item.name}>
                  <TopItemInfo>
                    <TopItemRank>{idx + 1}</TopItemRank>
                    <TopItemName>{item.name}</TopItemName>
                  </TopItemInfo>
                  <TopItemValue>
                    <TopItemCount>{item.count} продаж</TopItemCount>
                  </TopItemValue>
                </TopItem>
              ))}
            </TopList>
          )}
        </SectionContainer>

        {/* Покупатели */}
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Покупатели</SectionTitle>
            <div ref={buyersRef} style={{ position: 'relative' }}>
              <PeriodSelector onClick={() => setBuyersOpen(!buyersOpen)}>
                <PeriodText>{periodLabels[buyersPeriod]}</PeriodText>
                <ArrowDownIcon />
              </PeriodSelector>
              {buyersOpen && (
                <DropdownMenu>
                  <DropdownItem onClick={() => { setBuyersOpen(false); setBuyersPeriod('week'); }}>Неделя</DropdownItem>
                  <DropdownItem onClick={() => { setBuyersOpen(false); setBuyersPeriod('month'); }}>Месяц</DropdownItem>
                  <DropdownItem onClick={() => { setBuyersOpen(false); setBuyersPeriod('year'); }}>Год</DropdownItem>
                </DropdownMenu>
              )}
            </div>
          </SectionHeader>
          {buyersLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Loader size="small" noPadding />
            </div>
          ) : buyersData.length === 0 ? (
            <EmptyMessage>Нет статистики по покупателям</EmptyMessage>
          ) : (
            <TopList>
              {buyersData.sort((a, b) => b.count - a.count).map((item, idx) => (
                <TopItem key={item.name}>
                  <TopItemInfo>
                    <TopItemRank>{idx + 1}</TopItemRank>
                    <TopItemName>{item.name}</TopItemName>
                  </TopItemInfo>
                  <TopItemValue>
                    <TopItemCount>{item.count} продаж</TopItemCount>
                  </TopItemValue>
                </TopItem>
              ))}
            </TopList>
          )}
        </SectionContainer>
      </GridLayout>

      {/* Лучшие продажи - таблица с селектором */}
      <BestSalesTable>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <SectionTitle>Лучшие продажи</SectionTitle>
          <div ref={bestSalesRef} style={{ position: 'relative' }}>
            <PeriodSelector onClick={() => setBestSalesOpen(!bestSalesOpen)}>
              <PeriodText>{periodLabels[bestSalesPeriod]}</PeriodText>
              <ArrowDownIcon />
            </PeriodSelector>
            {bestSalesOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => createPeriodHandler(setBestSalesPeriod, loadBestSalesData, setBestSalesOpen)('week')}>
                  Неделя
                </DropdownItem>
                <DropdownItem onClick={() => createPeriodHandler(setBestSalesPeriod, loadBestSalesData, setBestSalesOpen)('month')}>
                  Месяц
                </DropdownItem>
                <DropdownItem onClick={() => createPeriodHandler(setBestSalesPeriod, loadBestSalesData, setBestSalesOpen)('year')}>
                  Год
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </div>
        {bestSalesLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
            <Loader size="medium" noPadding />
          </div>
        ) : bestSalesData.length === 0 ? (
          <EmptyMessage>
            Нет данных о продажах за данный период
          </EmptyMessage>
        ) : (
        <TableContainer>
          <Table>
            <THead>
              {table.getHeaderGroups().map(headerGroup => (
                <TR key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TH key={header.id} style={{ width: header.getSize() }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TH>
                  ))}
                </TR>
              ))}
            </THead>
            <TBody>
              {table.getRowModel().rows.map(row => (
                <TR key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TD key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
        )}
      </BestSalesTable>
    </StatsContainer>
  );
};

export default SalesAnalytics; 